import React, { useState, useEffect } from 'react';
import { Modal, Button, Input } from '@/components/ui';
import type { MaintenancePlan, PlanStatus, PriorityCode, FrequencyType } from '../types/maintenance-plan.types';
import { useMaintenancePlan } from '../hooks/useMaintenancePlan';
import { toast } from 'sonner';

interface MaintenancePlanFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  item?: MaintenancePlan | null;
  companyId: string;
}

const planStatuses: { value: PlanStatus; label: string }[] = [
  { value: 'draft', label: 'Bản nháp' },
  { value: 'pending_approval', label: 'Chờ duyệt' },
  { value: 'active', label: 'Đang áp dụng' },
  { value: 'paused', label: 'Tạm dừng' },
  { value: 'expired', label: 'Hết hạn' },
  { value: 'archived', label: 'Lưu trữ' },
];

const priorityOptions: { value: PriorityCode; label: string }[] = [
  { value: 'low', label: 'Thấp' },
  { value: 'medium', label: 'Trung bình' },
  { value: 'high', label: 'Cao' },
  { value: 'critical', label: 'Khẩn cấp' },
];

const frequencyOptions: { value: FrequencyType; label: string }[] = [
  { value: 'daily', label: 'Hàng ngày' },
  { value: 'weekly', label: 'Hàng tuần' },
  { value: 'monthly', label: 'Hàng tháng' },
  { value: 'quarterly', label: 'Hàng quý' },
  { value: 'yearly', label: 'Hàng năm' },
  { value: 'irregular', label: 'Không định kỳ' },
];

export function MaintenancePlanFormModal({ isOpen, onClose, item, companyId }: MaintenancePlanFormModalProps) {
  const isEdit = !!item;
  const { createPlan, updatePlan, isCreating, isUpdating } = useMaintenancePlan(companyId);

  const [formData, setFormData] = useState<Partial<MaintenancePlan>>({
    name: '',
    code: '',
    status_code: 'draft',
    priority_code: 'medium',
    plan_type: 'routine',
    frequency_type: 'weekly',
    frequency_interval: 1,
    target_scope_type: 'zone',
    start_date: '',
    effective_from: '',
    estimated_hours: 0,
    minimum_staff_count: 1,
    standard_staff_count: 2,
    project_id: '',
  });

  useEffect(() => {
    if (isOpen) {
      if (item) {
        setFormData(item);
      } else {
        setFormData({
          name: '',
          code: '',
          status_code: 'draft',
          priority_code: 'medium',
          plan_type: 'routine',
          frequency_type: 'weekly',
          frequency_interval: 1,
          target_scope_type: 'zone',
          start_date: new Date().toISOString().split('T')[0],
          effective_from: new Date().toISOString().split('T')[0],
          estimated_hours: 0,
          minimum_staff_count: 1,
          standard_staff_count: 2,
          project_id: '',
          company_id: companyId,
        });
      }
    }
  }, [isOpen, item, companyId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (isEdit && item) {
        await updatePlan({ id: item.id, updates: formData });
      } else {
        await createPlan({ ...formData, company_id: companyId });
      }
      onClose();
      toast.success(isEdit ? 'Cập nhật kế hoạch thành công' : 'Lập kế hoạch mới thành công');
    } catch (error: any) {
      toast.error('Lỗi: ' + error.message);
    }
  };

  const isPending = isCreating || isUpdating;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Cập nhật Kế hoạch Bảo dưỡng' : 'Lập Kế hoạch Bảo dưỡng mới'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-6 py-2">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Tên kế hoạch *"
            required
            value={formData.name || ''}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="VD: Bảo dưỡng cỏ Zone A"
          />
          <Input
            label="Mã kế hoạch *"
            required
            value={formData.code || ''}
            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
            placeholder="VD: PLN-001"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            label="ID Dự án *"
            required
            value={formData.project_id || ''}
            onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
          />
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Tần suất *</label>
            <select
              required
              className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-2.5 transition-all"
              value={formData.frequency_type || ''}
              onChange={(e) => setFormData({ ...formData, frequency_type: e.target.value as FrequencyType })}
            >
              {frequencyOptions.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <Input
            label="Khoảng cách (Kỳ)"
            type="number"
            min={1}
            value={formData.frequency_interval || 1}
            onChange={(e) => setFormData({ ...formData, frequency_interval: parseInt(e.target.value) })}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Mức độ ưu tiên *</label>
            <select
              required
              className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-2.5 transition-all"
              value={formData.priority_code || ''}
              onChange={(e) => setFormData({ ...formData, priority_code: e.target.value as PriorityCode })}
            >
              {priorityOptions.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Trạng thái *</label>
            <select
              required
              className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-2.5 transition-all"
              value={formData.status_code || ''}
              onChange={(e) => setFormData({ ...formData, status_code: e.target.value as PlanStatus })}
            >
              {planStatuses.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            label="Giờ dự tính (h)"
            type="number"
            value={formData.estimated_hours || 0}
            onChange={(e) => setFormData({ ...formData, estimated_hours: parseFloat(e.target.value) })}
          />
          <Input
            label="NS Tối thiểu"
            type="number"
            value={formData.minimum_staff_count || 1}
            onChange={(e) => setFormData({ ...formData, minimum_staff_count: parseInt(e.target.value) })}
          />
          <Input
            label="NS Chuẩn"
            type="number"
            value={formData.standard_staff_count || 2}
            onChange={(e) => setFormData({ ...formData, standard_staff_count: parseInt(e.target.value) })}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Ngày bắt đầu hiệu lực *"
            type="date"
            required
            value={formData.effective_from || ''}
            onChange={(e) => setFormData({ ...formData, effective_from: e.target.value })}
          />
          <Input
            label="Ngày kết thúc (Nếu có)"
            type="date"
            value={formData.effective_to || ''}
            onChange={(e) => setFormData({ ...formData, effective_to: e.target.value })}
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Ghi chú kế hoạch</label>
          <textarea
            className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-3 min-h-[80px]"
            placeholder="Các yêu cầu kỹ thuật đặc biệt hoặc lưu ý vận hành..."
            value={formData.notes || ''}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          />
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-slate-50">
          <Button variant="outline" type="button" onClick={onClose} disabled={isPending}>Hủy</Button>
          <Button variant="primary" type="submit" isLoading={isPending}>
            {isEdit ? 'Lưu thay đổi' : 'Tạo kế hoạch'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
